// Frontend-Spiegel der Backend-Renderer-Logik (server/services/templateRenderer.js).
// Genutzt fuer Live-Doku-Vorschau, damit der User vor dem Speichern sieht,
// was der Server rendern wird.

import {
    TreatmentNoteTemplate, TemplateSection, Indication, SupportedLocale,
} from '../../../services/apiService';
import { pickLocale } from '../../../utils/i18n';

function fillTemplate(tpl: string | undefined, vars: Record<string, string>): string {
    if (typeof tpl !== 'string') return '';
    return tpl.replace(/\{(\w+)\}/g, (_, key) => {
        const v = vars[key];
        return v === undefined || v === null ? '' : String(v);
    });
}

function isEmptyValue(v: any): boolean {
    if (v === undefined || v === null) return true;
    if (typeof v === 'string' && v.trim().length === 0) return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
}

export interface RenderContext {
    title?: string;
    practitioner_name?: string;
    patient_name?: string;
}

export function renderSection(
    section: TemplateSection,
    value: any,
    locale: SupportedLocale,
    fallback: SupportedLocale,
    indications: Indication[],
): string {
    if (isEmptyValue(value)) return '';
    const docTemplate = pickLocale(section.doc_template, locale, fallback);
    if (!docTemplate) return '';

    switch (section.type) {
        case 'single_choice': {
            const opt = (section.options || []).find(o => o.value === value);
            if (!opt) return '';
            return fillTemplate(docTemplate, {
                value: String(value),
                value_label: pickLocale(opt.label, locale, fallback),
            });
        }
        case 'multi_choice': {
            const values = Array.isArray(value) ? value : [value];
            const labels = values
                .map(v => {
                    const opt = (section.options || []).find(o => o.value === v);
                    return opt ? pickLocale(opt.label, locale, fallback) : null;
                })
                .filter(Boolean) as string[];
            if (labels.length === 0) return '';
            return fillTemplate(docTemplate, {
                values_csv: labels.join(', '),
                values_count: String(labels.length),
            });
        }
        case 'tooth_chart': {
            const teeth = (Array.isArray(value) ? value : [value])
                .map((t: any) => String(t).trim())
                .filter(Boolean);
            if (teeth.length === 0) return '';
            const sorted = teeth.slice().sort();
            return fillTemplate(docTemplate, {
                teeth_csv: sorted.join(', '),
                teeth_count: String(sorted.length),
            });
        }
        case 'text_freeform': {
            const text = String(value).trim();
            if (!text) return '';
            return fillTemplate(docTemplate, { text });
        }
        case 'chips_indication': {
            const values = Array.isArray(value) ? value : [value];
            const labels = values
                .map(v => {
                    const ind = indications.find(i => i.value === v);
                    return ind ? pickLocale(ind.label, locale, fallback) : null;
                })
                .filter(Boolean) as string[];
            if (labels.length === 0) return '';
            return fillTemplate(docTemplate, {
                values_csv: labels.join(', '),
                values_count: String(labels.length),
            });
        }
        default:
            return '';
    }
}

export function renderTemplate(
    template: TreatmentNoteTemplate,
    sectionValues: Record<string, any>,
    locale: SupportedLocale,
    ctx: RenderContext,
    indications: Indication[],
): string {
    if (!template) return '';
    const fallback = template.language_default || 'de';
    const parts: string[] = [];

    if (template.header_template && Object.keys(template.header_template).length > 0) {
        const headerTpl = pickLocale(template.header_template, locale, fallback);
        if (headerTpl) {
            const headerRendered = fillTemplate(headerTpl, ctx as Record<string, string>).trim();
            if (headerRendered) parts.push(headerRendered);
        }
    }

    for (const section of (template.sections || [])) {
        const rendered = renderSection(section, sectionValues[section.key], locale, fallback, indications);
        if (rendered) parts.push(rendered);
    }

    return parts.join('\n\n');
}
